import { useState, lazy, Suspense } from "react";
import { useNavigate } from "react-router";
import { EmptyState, Pagination, SendErrorReportButton } from "@/shared/components";
import { Prompt, PromptForm, Toast } from "@/shared/ui";
import { useAuth } from "@/shared/hooks";
import { PERMISSIONS } from "@/shared/consts";
import { HR_IMPORT_WORKER_PAGE_ROUTE } from "@/shared/utils";
import { useEmployeesListPage } from "../../hooks";
import type { WorkerListItem } from "../../types";
import {
  EmployeesCardsView,
  EmployeesFilter,
  EmployeesHeader,
  EmployeesPageSkeleton,
  EmployeesSummaryCards,
  EmployeesTableView,
} from "./components";

const PdfViewer = lazy(() => import("@/shared/ui/PdfViewer"));

const AssignRoleModal = lazy(() => import("./components/AssignRoleModal").then((m) => ({ default: m.default })));
const DepartmentsModal = lazy(() => import("@/features/hr/staffing/components/DepartmentsModal"));
const StaffingTableModal = lazy(() => import("@/features/hr/staffing/components/StaffingTableModal"));
const ManagersModal = lazy(() => import("./components/ManagersModal"));

export default function EmployeesPage() {
  const pageData = useEmployeesListPage();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();

  const [helpDismissed, setHelpDismissed] = useState(function getInitialDismissState() {
    return localStorage.getItem("employees-help-dismissed") === "true";
  });

  const canCreateEmployee = hasPermission(PERMISSIONS.HR.CREATE_EMPLOYEE);
  const canManageStructure = hasPermission(PERMISSIONS.HR.MANAGE_STRUCTURE);
  const canManageUsers = hasPermission(PERMISSIONS.ADMIN.MANAGE_USERS);

  const [isDepartmentsOpen, setIsDepartmentsOpen] = useState(false);
  const [isStaffingTableOpen, setIsStaffingTableOpen] = useState(false);
  const [isManagersOpen, setIsManagersOpen] = useState(false);

  if (pageData.isLoading && pageData.sortedWorkers.length === 0) {
    return (
      <>
        <title>{pageData.t("meta.title")}</title>
        <section className="flex-1 min-w-0">
          <EmployeesHeader
            showAddButton={canCreateEmployee}
            onAddClick={() => navigate(HR_IMPORT_WORKER_PAGE_ROUTE)}
          />
          <EmployeesPageSkeleton
            showToastPlaceholder={!helpDismissed}
            showCardActionPlaceholder={canManageStructure}
          />
        </section>
      </>
    );
  }

  if (pageData.isError) {
    return (
      <>
        <title>{pageData.t("meta.title")}</title>
        <section className="flex-1 min-w-0">
          <EmployeesHeader
            showAddButton={canCreateEmployee}
            onAddClick={() => navigate(HR_IMPORT_WORKER_PAGE_ROUTE)}
          />
          <p className="mt-4 text-body-regular-md content-action-negative">{pageData.t("messages.error")}</p>
          <SendErrorReportButton className="mt-3" />
        </section>
      </>
    );
  }

  return (
    <>
      <title>{pageData.t("meta.title")}</title>

      {pageData.isDownloadingProfile && pageData.downloadingId && (
        <Prompt
          loaderMode={true}
          onClose={() => {}}
          title=""
          text=""
          loaderText={pageData.t("messages.loader.waitingForDownload.text")}
          additionalText={pageData.t("messages.loader.waitingForDownload.additionalText")}
          namespace="EmployeesPage"
        />
      )}

      {pageData.isRehiring && (
        <Prompt
          loaderMode={true}
          onClose={() => {}}
          title=""
          text=""
          loaderText={pageData.t("messages.loader.waitingForRehire.text")}
          additionalText={pageData.t("messages.loader.waitingForRehire.additionalText")}
          namespace="EmployeesPage"
        />
      )}

      {pageData.prompt && (
        <Prompt
          title={pageData.prompt.title}
          text={pageData.prompt.text}
          variant={pageData.prompt.variant || "success"}
          onClose={() => pageData.setPrompt(null)}
        />
      )}

      {pageData.confirmDeleteDraftId !== null && (
        <PromptForm
          title={pageData.t("draft.deleteConfirmTitle")}
          text={pageData.t("draft.deleteConfirmText")}
          variant="error"
          confirmText={pageData.t("draft.deleteConfirm")}
          cancelText={pageData.t("draft.cancel")}
          isLoading={pageData.isDeletingDraft}
          onConfirm={() => pageData.handleDeleteDraftConfirm(pageData.confirmDeleteDraftId!)}
          onClose={() => pageData.setConfirmDeleteDraftId(null)}
          namespace="EmployeesPage"
        />
      )}

      {pageData.assignRoleEmployee && (
        <Suspense fallback={null}>
          <AssignRoleModal
            employee={pageData.assignRoleEmployee}
            onClose={() => pageData.setAssignRoleEmployee(null)}
            onConfirm={(roles) => pageData.handleSyncRoles(roles)}
            isLoading={pageData.isSyncingRoles}
          />
        </Suspense>
      )}

      {isDepartmentsOpen && (
        <Suspense fallback={null}>
          <DepartmentsModal onClose={() => setIsDepartmentsOpen(false)} />
        </Suspense>
      )}

      {isStaffingTableOpen && (
        <Suspense fallback={null}>
          <StaffingTableModal onClose={() => setIsStaffingTableOpen(false)} />
        </Suspense>
      )}

      {isManagersOpen && (
        <Suspense fallback={null}>
          <ManagersModal onClose={() => setIsManagersOpen(false)} />
        </Suspense>
      )}

      {pageData.previewUrl && (
        <Suspense fallback={null}>
          <PdfViewer
            fileUrl={pageData.previewUrl}
            fileName={pageData.previewFileName || undefined}
            title={pageData.previewTitle}
            onClose={pageData.closePreview}
          />
        </Suspense>
      )}

      <section className="flex-1 min-w-0">
        <EmployeesHeader
          showAddButton={canCreateEmployee}
          onAddClick={() => navigate(HR_IMPORT_WORKER_PAGE_ROUTE)}
          onManagersClick={canManageUsers ? () => setIsManagersOpen(true) : undefined}
        />

        <EmployeesSummaryCards
          stats={pageData.stats}
          showStructureActions={canManageStructure}
          onDepartmentsClick={() => setIsDepartmentsOpen(true)}
          onStaffingTableClick={() => setIsStaffingTableOpen(true)}
        />

        {!helpDismissed && (
          <div className="mb-4">
            <Toast
              color="grey"
              text={pageData.t("toast.helpTitle")}
              additionalText={pageData.t("toast.helpText")}
              closable
              autoClose={false}
              isFullWidth
              action={{
                label: pageData.t("toast.helpDismiss"),
                onClick() {
                  localStorage.setItem("employees-help-dismissed", "true");
                  setHelpDismissed(true);
                },
              }}
              onClose={() => setHelpDismissed(true)}
            />
          </div>
        )}

        <div className="radius-3xl surface-base-fill p-7">
          <EmployeesFilter
            search={pageData.search}
            onSearchChange={pageData.setSearch}
            hireDateRange={pageData.hireDateRange}
            onHireDateRangeChange={pageData.setHireDateRange}
            statusFilter={pageData.statusFilter}
            onStatusChange={pageData.setStatusFilter}
            statusOptions={pageData.statusOptions}
            socialCategoryFilter={pageData.socialCategoryFilter}
            onSocialCategoryChange={pageData.setSocialCategoryFilter}
            socialCategoryOptions={pageData.socialCategoryOptions}
            hasActiveFilters={pageData.activeFilters}
            onReset={pageData.handleResetFilters}
            locale={pageData.locale}
            view={pageData.view}
            onViewChange={pageData.setView}
          />

          {pageData.sortedWorkers.length === 0 && pageData.importDrafts.length === 0 ? (
            <EmptyState
              hasActiveFilters={pageData.activeFilters}
              noResultsText={pageData.t("table.noResults")}
              noDataText={pageData.t("table.noData")}
              noDataHint={pageData.t("table.noDataHint")}
            />
          ) : (
            <>
              {pageData.view === "table" ? (
                <EmployeesTableView
                  employees={pageData.pageWorkers}
                  importDrafts={pageData.pageDrafts}
                  sortConfig={pageData.sortConfig}
                  onSort={pageData.handleSort}
                  onDownloadProfile={pageData.handleDownloadProfile}
                  isDownloading={pageData.isDownloadingProfile}
                  onOpenAssignRole={(employee) => pageData.setAssignRoleEmployee(employee)}
                  onRehire={(employee) => pageData.handleRehire(employee)}
                  isRehiring={pageData.isRehiring}
                  onSelectEmployee={(emp: WorkerListItem) => navigate(`/hr/employees/${emp.id}`)}
                  onDeleteDraft={canCreateEmployee ? (id) => pageData.setConfirmDeleteDraftId(id) : undefined}
                />
              ) : (
                <EmployeesCardsView
                  employees={pageData.pageWorkers}
                  importDrafts={pageData.pageDrafts}
                  onDownloadProfile={pageData.handleDownloadProfile}
                  isDownloading={pageData.isDownloadingProfile}
                  onOpenAssignRole={(employee) => pageData.setAssignRoleEmployee(employee)}
                  onRehire={(employee) => pageData.handleRehire(employee)}
                  isRehiring={pageData.isRehiring}
                  onSelectEmployee={(emp: WorkerListItem) => navigate(`/hr/employees/${emp.id}`)}
                  onDeleteDraft={canCreateEmployee ? (id) => pageData.setConfirmDeleteDraftId(id) : undefined}
                />
              )}

              <Pagination
                currentPage={pageData.pagination.page}
                totalPages={pageData.pagination.totalPages}
                onPageChange={pageData.pagination.setPage}
                fromItem={pageData.pagination.fromItem}
                toItem={pageData.pagination.toItem}
                total={pageData.totalCount}
              />
            </>
          )}
        </div>
      </section>
    </>
  );
}
